/**
 * Pause break helper for Reveal pause screen.
 * Adds subtle quick buttons (5/10/15) and optional countdown with auto-resume.
 */

const BREAK_MINUTES_PRESETS = [5, 10, 15] as const;
const ROOT_ID = 'lectura-pause-break-root';
const PARTICLES_ID = 'lectura-pause-break-particles';
const FUN_LINES = [
  'Perfect moment for a sip of water.',
  'Stand up and stretch your shoulders for a minute.',
  'Take a short walk and come back refreshed.',
  'Rest your eyes and look away from the screen.',
  'Small break now, sharper focus after.',
  'Deep breath in, slow breath out.',
  'Time for a quick coffee or tea refill.',
  'Loosen your neck and wrists for a few seconds.',
  'Pause complete? We will continue soon.',
  'Great progress so far. Enjoy this short break.',
  'Quick posture check: feet grounded, shoulders relaxed.',
  'Take a calm minute and reset your attention.',
  'Refill your glass if needed.',
  'A short reset helps ideas land better.',
  'If needed, this is a good moment for a quick stop.',
  'Come back with fresh energy.',
  'Take a breath, then one more.',
  'Short break, big clarity.',
  'You are doing great. See you in a moment.',
  'Relax your jaw and blink a few times.'
] as const;

let tickTimer: ReturnType<typeof setInterval> | null = null;
let uiSyncTimer: ReturnType<typeof setInterval> | null = null;
let breakEndsAt = 0;
let breakDurationMs = 0;
let breakFinishedAwaitingResume = false;
let delegatedClickInstalled = false;
let funLineCurrent = '';
let nextFunLineAt = 0;

function pauseBreakDebug(message: string, data?: Record<string, unknown>): void {
  if (typeof console !== 'undefined' && typeof console.debug === 'function') {
    console.debug(`[lectura][pause-break] ${message}`, data || {});
  }
}

function isRevealPaused(): boolean {
  const r = (typeof Reveal !== 'undefined' ? Reveal : undefined) as { isPaused?: () => boolean } | undefined;
  return !!r?.isPaused?.();
}

function setRevealPaused(paused: boolean): void {
  const r = (typeof Reveal !== 'undefined' ? Reveal : undefined) as
    | { isPaused?: () => boolean; togglePause?: (override?: boolean) => void }
    | undefined;
  const currentlyPaused = !!r?.isPaused?.();
  pauseBreakDebug('setRevealPaused called', { requested: paused, currentlyPaused });
  if (r?.togglePause) {
    if (paused && !currentlyPaused) {
      pauseBreakDebug('togglePause -> pause');
      r.togglePause();
    }
    if (!paused && currentlyPaused) {
      pauseBreakDebug('togglePause -> resume');
      r.togglePause();
    }
    pauseBreakDebug('after togglePause', { nowPaused: !!r?.isPaused?.() });
    return;
  }
  // Fallback for environments where only keyboard toggle is available.
  if (currentlyPaused === paused) return;
  pauseBreakDebug('fallback dispatch keydown:b', { targetPaused: paused });
  document.dispatchEvent(new KeyboardEvent('keydown', { key: 'b', bubbles: true, cancelable: true }));
}

function resolvePauseOverlay(): HTMLElement | null {
  return document.querySelector<HTMLElement>('.reveal > .pause-overlay');
}

function formatClock(ts: number): string {
  try {
    return new Intl.DateTimeFormat([], { hour: '2-digit', minute: '2-digit' }).format(new Date(ts));
  } catch {
    const d = new Date(ts);
    const hh = String(d.getHours()).padStart(2, '0');
    const mm = String(d.getMinutes()).padStart(2, '0');
    return `${hh}:${mm}`;
  }
}

function formatRemaining(ms: number): string {
  const secs = Math.max(0, Math.ceil(ms / 1000));
  const mm = Math.floor(secs / 60);
  const ss = secs % 60;
  return `${String(mm).padStart(2, '0')}:${String(ss).padStart(2, '0')}`;
}

function clearBreakTimer(): void {
  breakEndsAt = 0;
  breakDurationMs = 0;
  breakFinishedAwaitingResume = false;
  funLineCurrent = '';
  nextFunLineAt = 0;
  if (tickTimer) {
    clearInterval(tickTimer);
    tickTimer = null;
  }
}

function setOverlayIntensity(overlay: HTMLElement, intensity: number): void {
  const clamped = Math.max(0, Math.min(1, intensity));
  overlay.style.setProperty('--rf-break-intensity', clamped.toFixed(3));
  if (clamped > 0) overlay.classList.add('rf-pause-break-bg-active');
  else overlay.classList.remove('rf-pause-break-bg-active');
}

function setOverlayFillProgress(overlay: HTMLElement, progress: number): void {
  const clamped = Math.max(0, Math.min(1, progress));
  overlay.style.setProperty('--rf-break-fill', clamped.toFixed(3));
}

function ensureParticleLayer(overlay: HTMLElement): void {
  let layer = overlay.querySelector<HTMLElement>(`#${PARTICLES_ID}`);
  if (layer) return;
  layer = document.createElement('div');
  layer.id = PARTICLES_ID;
  layer.className = 'rf-pause-break-particles';
  const COUNT = 84;
  for (let i = 0; i < COUNT; i++) {
    const p = document.createElement('span');
    p.className = 'rf-pause-break-particle';
    p.style.setProperty('--x', `${Math.random() * 100}`);
    p.style.setProperty('--s', `${1 + Math.random() * 3.2}`);
    p.style.setProperty('--o', `${0.35 + Math.random() * 0.85}`);
    p.style.setProperty('--b', `${Math.random() * 2.2}`);
    p.style.setProperty('--dur', `${7 + Math.random() * 14}`);
    p.style.setProperty('--delay', `${-1 * Math.random() * 16}`);
    p.style.setProperty('--drift', `${(Math.random() - 0.5) * 24}`);
    layer.appendChild(p);
  }
  overlay.insertBefore(layer, overlay.firstChild);
}

function pickRandomFunLine(): string {
  if (FUN_LINES.length === 0) return '';
  if (FUN_LINES.length === 1) return FUN_LINES[0]!;
  let candidate = FUN_LINES[Math.floor(Math.random() * FUN_LINES.length)]!;
  if (candidate === funLineCurrent) {
    candidate = FUN_LINES[(FUN_LINES.indexOf(candidate) + 1) % FUN_LINES.length]!;
  }
  return candidate;
}

function resetBreakUiState(): void {
  const overlay = resolvePauseOverlay();
  if (!overlay) return;
  setOverlayIntensity(overlay, 0);
  setOverlayFillProgress(overlay, 0);
  const root = overlay.querySelector<HTMLElement>(`#${ROOT_ID}`);
  if (!root) return;
  const presets = root.querySelector<HTMLElement>('.rf-pause-break-presets');
  const live = root.querySelector<HTMLElement>('.rf-pause-break-live');
  const countdownEl = root.querySelector<HTMLElement>('.rf-pause-break-countdown');
  const resumeEl = root.querySelector<HTMLElement>('.rf-pause-break-resume');
  const funEl = root.querySelector<HTMLElement>('.rf-pause-break-fun');

  root.classList.remove('rf-pause-break--active');
  if (presets) presets.hidden = false;
  if (live) live.hidden = true;
  if (countdownEl) countdownEl.textContent = '--:--';
  if (resumeEl) resumeEl.textContent = 'We continue at --:--';
  if (funEl) {
    funEl.hidden = true;
    funEl.textContent = '';
  }
}

function renderBreakUi(): void {
  if (!isRevealPaused()) {
    pauseBreakDebug('renderBreakUi: reveal no longer paused, clearing timer', { breakEndsAt });
    clearBreakTimer();
    resetBreakUiState();
    return;
  }
  const overlay = resolvePauseOverlay();
  if (!overlay) return;
  ensureParticleLayer(overlay);

  let root = overlay.querySelector<HTMLElement>(`#${ROOT_ID}`);
  if (!root) {
    root = document.createElement('div');
    root.id = ROOT_ID;
    root.className = 'rf-pause-break';
    root.innerHTML =
      '<div class="rf-pause-break-inner">' +
      '<div class="rf-pause-break-title">Break timer</div>' +
      '<div class="rf-pause-break-row rf-pause-break-presets">' +
      BREAK_MINUTES_PRESETS.map((m) => `<button type="button" class="rf-pause-break-btn" data-rf-break="${m}">${m} min</button>`).join('') +
      '</div>' +
      '<div class="rf-pause-break-row rf-pause-break-live" hidden>' +
      '<span class="rf-pause-break-countdown">--:--</span>' +
      '<span class="rf-pause-break-resume">We continue at --:--</span>' +
      '<span class="rf-pause-break-fun" hidden></span>' +
      '</div>' +
      '</div>';
    overlay.appendChild(root);

    // Clicking inside this UI must not close Reveal's pause screen.
    const stopPauseToggleBubble = (ev: Event): void => {
      ev.stopPropagation();
    };
    root.addEventListener('click', stopPauseToggleBubble, true);
    root.addEventListener('mousedown', stopPauseToggleBubble, true);
    root.addEventListener('pointerdown', stopPauseToggleBubble, true);
    root.addEventListener('touchstart', stopPauseToggleBubble, { capture: true });

    root.querySelectorAll<HTMLButtonElement>('[data-rf-break]').forEach((btn) => {
      btn.addEventListener('click', (ev) => {
        ev.stopPropagation();
        const mins = parseInt(btn.dataset.rfBreak || '', 10);
        pauseBreakDebug('break button click', {
          minutesRaw: btn.dataset.rfBreak || '',
          parsedMinutes: mins,
          pausedBeforeClick: isRevealPaused()
        });
        if (Number.isFinite(mins) && mins > 0) startPauseBreak(mins);
      });
      btn.addEventListener('pointerdown', () => {
        pauseBreakDebug('break button pointerdown', {
          minutesRaw: btn.dataset.rfBreak || '',
          pausedAtPointerDown: isRevealPaused()
        });
      });
    });
  }

  const presets = root.querySelector<HTMLElement>('.rf-pause-break-presets');
  const live = root.querySelector<HTMLElement>('.rf-pause-break-live');
  const countdownEl = root.querySelector<HTMLElement>('.rf-pause-break-countdown');
  const resumeEl = root.querySelector<HTMLElement>('.rf-pause-break-resume');
  const funEl = root.querySelector<HTMLElement>('.rf-pause-break-fun');
  if (!presets || !live || !countdownEl || !resumeEl || !funEl) return;

  if (breakFinishedAwaitingResume) {
    root.classList.add('rf-pause-break--active');
    presets.hidden = true;
    live.hidden = false;
    countdownEl.textContent = 'Soon';
    resumeEl.textContent = 'We will continue soon.';
    funEl.hidden = true;
    funEl.textContent = '';
    setOverlayIntensity(overlay, 1);
    setOverlayFillProgress(overlay, 1);
    return;
  }

  if (breakEndsAt <= Date.now()) {
    root.classList.remove('rf-pause-break--active');
    presets.hidden = false;
    live.hidden = true;
    countdownEl.textContent = '--:--';
    resumeEl.textContent = 'We continue at --:--';
    funEl.hidden = true;
    funEl.textContent = '';
    setOverlayIntensity(overlay, 0);
    setOverlayFillProgress(overlay, 0);
    return;
  }
  root.classList.add('rf-pause-break--active');
  presets.hidden = true;
  live.hidden = false;
  const remaining = breakEndsAt - Date.now();
  countdownEl.textContent = formatRemaining(remaining);
  resumeEl.textContent = `We continue at ${formatClock(breakEndsAt)}`;
  const now = Date.now();
  if (!funLineCurrent || now >= nextFunLineAt) {
    funLineCurrent = pickRandomFunLine();
    nextFunLineAt = now + 60_000;
  }
  funEl.textContent = funLineCurrent;
  funEl.hidden = false;
  const progress = breakDurationMs > 0 ? 1 - remaining / breakDurationMs : 0;
  // Hold some baseline ambiance while break is active; ramp up near resume.
  const intensity = 0.18 + Math.max(0, Math.min(1, progress)) * 0.82;
  setOverlayIntensity(overlay, intensity);
  setOverlayFillProgress(overlay, progress);
}

export function startPauseBreak(minutes: number): void {
  const mins = Math.max(1, Math.min(60, Math.round(minutes)));
  pauseBreakDebug('startPauseBreak', { requestedMinutes: minutes, clampedMinutes: mins });
  breakFinishedAwaitingResume = false;
  setRevealPaused(true);
  breakDurationMs = mins * 60_000;
  breakEndsAt = Date.now() + breakDurationMs;
  pauseBreakDebug('timer armed', { breakEndsAt, resumeAt: formatClock(breakEndsAt) });
  renderBreakUi();
  if (!tickTimer) {
    tickTimer = setInterval(() => {
      if (!isRevealPaused()) {
        pauseBreakDebug('tick: reveal unpaused externally, clear timer');
        clearBreakTimer();
        return;
      }
      if (breakEndsAt > 0 && Date.now() >= breakEndsAt) {
        pauseBreakDebug('tick: timer elapsed, switching to continue-soon state');
        breakEndsAt = 0;
        breakDurationMs = 0;
        breakFinishedAwaitingResume = true;
        if (tickTimer) {
          clearInterval(tickTimer);
          tickTimer = null;
        }
        renderBreakUi();
        return;
      }
      renderBreakUi();
    }, 1000);
  }
}

export function setPauseScreen(paused: boolean): void {
  if (!paused) {
    clearBreakTimer();
    resetBreakUiState();
  }
  setRevealPaused(paused);
}

export function installPauseBreakUi(): void {
  if (!delegatedClickInstalled) {
    delegatedClickInstalled = true;
    document.addEventListener(
      'click',
      (ev) => {
        const target = ev.target as HTMLElement | null;
        const btn = target?.closest?.('[data-rf-break]') as HTMLButtonElement | null;
        if (!btn) return;
        const mins = parseInt(btn.dataset.rfBreak || '', 10);
        pauseBreakDebug('delegated break click', {
          minutesRaw: btn.dataset.rfBreak || '',
          parsedMinutes: mins,
          pausedAtDelegatedClick: isRevealPaused()
        });
        ev.preventDefault();
        ev.stopPropagation();
        if (Number.isFinite(mins) && mins > 0) startPauseBreak(mins);
      },
      true
    );
  }
  if (uiSyncTimer) return;
  uiSyncTimer = setInterval(() => {
    // Always run: renderBreakUi() also handles reset when pause is closed.
    renderBreakUi();
  }, 400);
}

